// Layout calculation types and utilities. Primarily concerned with calculating
// layout statistics

export interface FrameLayoutInfo {
  childCount: number;

  // Frame dimensions
  frameWidth: number | null;
  frameHeight: number | null;
  effectiveWidth: number;
  effectiveHeight: number;
  maxSize: { width: number | null; height: number | null };

  // Spacing
  padding: SpacingSidesStruct;
  gap: number; // Legacy uniform gap - kept for backward compatibility
  individualGaps: number[]; // Array of gaps between each pair of adjacent children
  edgeGapBefore: number; // Gap before first object (layoutGapBefore on first child)
  edgeGapAfter: number; // Gap after last object (layoutGapAfter on last child)

  // Child measurements
  maxChildWidth: number;
  maxChildHeight: number;

  // TODO better name
  total: { width: number; height: number };
}

// Compute statistics for the layout.
export function calculateFrameLayout(frame: any): FrameLayoutInfo {
  const children = frame.sortedChildren();
  const padding = frame.layoutPaddingSides();
  const isHorizontal = frame.layoutDirection === 'horizontal';

  // Get the size which controls how other settings will apply.
  const [elementWidth, elementHeight] = frame.layoutSize();
  let [maxWidth, maxHeight] = [elementWidth, elementHeight];

  if (maxWidth) maxWidth -= padding.left + padding.right;
  if (maxHeight) maxHeight -= padding.top + padding.bottom;

  const maxSize = { width: maxWidth, height: maxHeight };

  // Track total used space of children and gap and maximums
  const total = { width: 0, height: 0 };
  const maxChild = { width: 0, height: 0 };

  children.forEach(child => {
    let childWidth = child.width * child.scaleX;
    let childHeight = child.height * child.scaleY;

    if (child.strokeWidth) {
      childWidth += child.strokeWidth;
      childHeight += child.strokeWidth;
    }

    total.width += childWidth;
    total.height += childHeight;
    maxChild.width = Math.max(maxChild.width, childWidth);
    maxChild.height = Math.max(maxChild.height, childHeight);
  });

  if (isHorizontal) {
    if (!maxSize.height) {
      maxSize.height = maxChild.height;
    }
  } else {
    if (!maxSize.width) {
      maxSize.width = maxChild.width;
    }
  }

  // Calculate individual gaps between adjacent children and edge gaps
  const individualGaps: number[] = [];
  let totalGapSpace = 0;
  let edgeGapBefore = 0; // Gap before first object
  let edgeGapAfter = 0; // Gap after last object

  // Check if any children have individual gap settings
  const hasIndividualGaps = children.some(
    child => child.layoutGapBefore != null || child.layoutGapAfter != null
  );

  if (hasIndividualGaps && children.length > 0) {
    // Calculate edge gaps
    edgeGapBefore = children[0]?.layoutGapBefore ?? 0;
    edgeGapAfter = children[children.length - 1]?.layoutGapAfter ?? 0;
    totalGapSpace += edgeGapBefore + edgeGapAfter;
  }

  for (let i = 0; i < children.length - 1; i++) {
    const currentChild = children[i];
    const nextChild = children[i + 1];

    let gapBetween = 0;

    if (hasIndividualGaps) {
      // Use individual gaps with additive behavior
      const gapAfter = currentChild.layoutGapAfter ?? 0;
      const gapBefore = nextChild.layoutGapBefore ?? 0;
      gapBetween = gapAfter + gapBefore;
    } else {
      // Fall back to global gap behavior
      if (
        frame.layoutGap === 'auto' &&
        maxWidth &&
        maxHeight &&
        children.length > 1
      ) {
        // Auto gaps will be calculated after we know total space needed
        gapBetween = 0; // Will be set below
      } else if (frame.layoutGap !== 'auto') {
        gapBetween = frame.layoutGap;
      }
    }

    individualGaps.push(gapBetween);
    totalGapSpace += gapBetween;
  }

  // Handle auto gaps when no individual gaps are set
  let gap = 0; // Legacy uniform gap for backward compatibility
  if (!hasIndividualGaps && frame.layoutGap === 'auto') {
    const mainAxisSize = isHorizontal ? maxWidth : maxHeight;
    const mainAxisTotal = isHorizontal ? total.width : total.height;

    if (mainAxisSize && children.length > 1) {
      gap = (mainAxisSize - mainAxisTotal) / (children.length - 1);
      // Update individual gaps array with the calculated auto gap
      for (let i = 0; i < individualGaps.length; i++) {
        individualGaps[i] = gap;
      }
      totalGapSpace = gap * (children.length - 1);
    }
  } else if (!hasIndividualGaps && frame.layoutGap !== 'auto') {
    gap = frame.layoutGap;
  }

  // Add total gap space to the total dimensions
  if (isHorizontal) {
    total.width += totalGapSpace;
  } else {
    total.height += totalGapSpace;
  }

  return {
    childCount: children.length,

    frameWidth: elementWidth,
    frameHeight: elementHeight,
    effectiveWidth: maxWidth ?? 0,
    effectiveHeight: maxHeight ?? 0,
    maxSize,

    padding,
    gap,
    individualGaps,
    edgeGapBefore,
    edgeGapAfter,

    total,
    maxChildWidth: maxChild.width,
    maxChildHeight: maxChild.height,
  };
}
